import { Navigate, useLocation, useParams } from "react-router-dom";
import {
  getContentTypeFromSlug,
  getContentTypeSlug,
  getStatusOptions,
  getStatusSlug,
} from "../utils/utils";
import Track from "./Track";
import NotFoundPage from "./NotFoundPage";

// Status slugs that can appear in a track URL. Derived from getStatusOptions
// rather than listed out, so adding a status can't leave a legitimate URL
// 404ing here. Excludes "none" for the same reason the header's picker does —
// untracked content has no library page to show.
const TRACK_STATUS_SLUGS = new Set(
  Object.keys(getStatusOptions())
    .filter((value) => value !== "none")
    .map(getStatusSlug),
);

/**
 * Route guard for /track/u/:username/:contentType/:status and, for All
 * content types, /track/u/:username/:status.
 *
 * Track reads both params straight out of the URL and indexes option maps with
 * them, so an unrecognised segment used to render `undefined.icon` and take the
 * whole page down. Validation lives here rather than in Track itself because
 * Track calls a long list of hooks before it renders — an early return part-way
 * through would break hook ordering when a URL flips between valid and invalid.
 *
 * /track/u/<user>/movies|tv|books/<status-slug>  → Track
 * /track/u/<user>/<status-slug>                  → Track (All)
 * /track/u/<user>/movie|tvshow|book/<...>        → redirect: legacy content-type value
 * /track/u/<user>/[...]/abandoned                → redirect: legacy status value ("stopped" is the slug)
 * anything else                                  → NotFoundPage
 */
export default function TrackDispatcher() {
  const { username, contentType: contentTypeSlug, status: statusSlug } =
    useParams();
  const { search } = useLocation();

  // Each segment may already be in its current, canonical form, or it may
  // carry the pre-slug raw enum value (content type) / raw status value
  // (status) from a bookmark, home-screen shortcut or shared link. Resolve
  // both independently before deciding whether a redirect is needed. No
  // content type segment means All, which has nothing to resolve.
  const isAll = contentTypeSlug === undefined;
  const canonicalContentTypeSlug =
    isAll || getContentTypeFromSlug(contentTypeSlug)
      ? contentTypeSlug
      : getContentTypeSlug(contentTypeSlug);
  const canonicalStatusSlug = TRACK_STATUS_SLUGS.has(statusSlug)
    ? statusSlug
    : getStatusSlug(statusSlug);

  if (
    (!isAll && !canonicalContentTypeSlug) ||
    !TRACK_STATUS_SLUGS.has(canonicalStatusSlug)
  ) {
    return <NotFoundPage />;
  }

  if (
    canonicalContentTypeSlug !== contentTypeSlug ||
    canonicalStatusSlug !== statusSlug
  ) {
    const contentSegment = isAll ? "" : `/${canonicalContentTypeSlug}`;
    return (
      <Navigate
        to={`/track/u/${username}${contentSegment}/${canonicalStatusSlug}${search}`}
        replace
      />
    );
  }

  return <Track />;
}
